import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CreditCardIcon } from "@phosphor-icons/react";
import { CardInfo } from "@shared/components";
import { RepassTabs, PaymentMethodTabs, CardNetwork } from "./components";
import { ICompanyPlanTaxCreditMethod } from "@shared/types";

import type { CardCreditCardProps } from "./card-credit-card.type";
import styles from "./card-credit-card.module.css";

export const CardCreditCard = ({
    data,
    isLoading,
    onChangeData
}: CardCreditCardProps) => {
    const { t } = useTranslation();

    const delays = Object.keys(data || {}).sort();
    const [activeDelay, setActiveDelay] = useState(delays[0] || "default");
    const [activePaymentMethod, setActivePaymentMethod] = useState<"online" | "pos" | "tap">("online");

    const handleAddDelay = (delay: string) => {
        const newData = { ...(data || {}) };
        if (!newData[delay]) {
            newData[delay] = {};
            onChangeData(newData);
            setActiveDelay(delay);
        }
    };

    const handleDeleteDelay = (delay: string) => {
        const newData = { ...(data || {}) };
        delete newData[delay];
        onChangeData(newData);
        if (activeDelay === delay) {
            setActiveDelay(Object.keys(newData)[0] || "default");
        }
    };

    const handleNetworkChange = (networkData: ICompanyPlanTaxCreditMethod) => {
        const newData = { ...(data || {}) };
        const delayData = { ...(newData[activeDelay] || {}) };

        delayData[activePaymentMethod] = networkData;
        newData[activeDelay] = delayData;

        onChangeData(newData);
    };

    const title = (
        <div className={styles["card-header"]}>
            <CreditCardIcon /> {t("backoffice.pageModalCompanyPlanCreditTitle")}
        </div>
    ) as unknown as string;

    return (
        <CardInfo
            title={title}
            isLoading={isLoading}
            variation="one-column"
            info={[
                {
                    label: "",
                    className: styles["card-info-item"],
                    value: (
                        <div className={styles["card-content"]}>
                            <RepassTabs
                                delays={delays}
                                activeDelay={activeDelay}
                                onDelayChange={setActiveDelay}
                                onAddDelay={handleAddDelay}
                                onDeleteDelay={handleDeleteDelay}
                                isLoading={isLoading}
                            />

                            <PaymentMethodTabs
                                activeMethod={activePaymentMethod}
                                onMethodChange={setActivePaymentMethod}
                                isLoading={isLoading}
                            />

                            <CardNetwork
                                data={data?.[activeDelay]?.[activePaymentMethod]}
                                onChange={handleNetworkChange}
                                isLoading={isLoading}
                            />
                        </div>
                    ),
                },
            ]}
        />
    );
};
